import { memo, useMemo } from "react";
import { _ } from "@sablier/v2-mixins";
import { getTheme } from "@sablier/v2-themes";
import Base from "antd/lib/popover";
import type { ThemeType } from "@sablier/v2-themes";
import type { ComponentProps, PropsWithChildren, ReactNode } from "react";
import type { CSSProperties } from "react";

const theme = getTheme();

interface Props {
  arrow?: ComponentProps<typeof Base>["arrow"];
  autoAdjustOverflow?: ComponentProps<typeof Base>["autoAdjustOverflow"];
  color?: keyof ThemeType["colors"];
  container?: CSSProperties;
  maxWidth?: `${string}px`;
  mouseEnterDelay?: number;
  mouseLeaveDelay?: number;
  placement?: ComponentProps<typeof Base>["placement"];
  trigger?:
    | ComponentProps<typeof Base>["trigger"]
    | "hover"
    | "focus"
    | "click"
    | "contextMenu";
  value?: string | ReactNode;
}

const base: CSSProperties = {
  borderRadius: "10px",
};

function Tooltip({
  arrow,
  autoAdjustOverflow = true,
  color = "dark050",
  children,
  maxWidth = "300px",
  mouseEnterDelay = 0,
  mouseLeaveDelay = 0.1,
  placement,
  trigger = "hover",
  value = null,
  container = {},
}: PropsWithChildren<Props>) {
  const background = useMemo(() => theme.colors[color], [color]);
  const style = useMemo(
    () => ({ ...base, maxWidth, ...container }),
    [container, maxWidth],
  );

  if (_.isNilOrEmptyString(value)) {
    return <>{children}</>;
  }

  return (
    <Base
      arrow={arrow}
      color={background}
      content={value}
      trigger={trigger}
      overlayInnerStyle={style}
      mouseEnterDelay={mouseEnterDelay}
      mouseLeaveDelay={mouseLeaveDelay}
      autoAdjustOverflow={autoAdjustOverflow}
      placement={placement}
      zIndex={theme.sizes.zIndexTooltip}
    >
      {children}
    </Base>
  );
}

export default memo(Tooltip);
